import React, { useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  KDialog,
  KTextInput,
  KSelectAutocomplete,
} from "@kyburz/kdesign-system";
import { languages } from "../constants/languages";
import { timezones } from "../constants/timezones";
import useSetState from "../../hooks/useTypedSetState";
import { humanize } from "../../utils/utils";
import { createCustomer } from "../../apiCalls/auth";
import { setAlert } from "../../redux/actions/actions";
import { getRealms } from "../../apiCalls/realm";
import { Realm } from "../../interfaces/RealmInterface";
import { Customer2 } from "../../interfaces/ReduxInterface";
import { useStyles } from "./styles";

interface Props {
  open: boolean;
  handleNewCompanyPopup: (value) => void;
  handleClickCustomer: (value) => void;
}

const NewCustomer: React.FC<Props> = ({
  open,
  handleNewCompanyPopup,
  handleClickCustomer,
}: Props) => {
  const [
    { shortname, name, language, timezone, realm, realmList, createdCustomer },
    setState,
  ] = useSetState({
    shortname: "",
    name: "",
    language: { label: "german", value: "de" },
    timezone: "Europe/Zurich",
    realm: "",
    realmList: [] as Realm[],
    createdCustomer: null as Customer2 | null,
  });
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const handleChange = (e) => setState({ [e.target.name]: e.target.value });

  useEffect(() => {
    getRealms().then((res) => {
      if (res && res.data && Array.isArray(res.data))
        setState({ realmList: res.data });
    });
  }, []);

  const realmsOptions = useMemo(
    () =>
      realmList.map(({ realm: realmName }) => ({
        label: realmName,
        value: realmName,
      })),
    [realmList],
  );

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const handleSelectChange = (name, value): void => {
    if (name === "language") setState({ [name]: value });
    else setState({ [name]: value?.label });
  };

  const handleCustomerSwitch = (customer) => {
    setState({ createdCustomer: null });
    handleNewCompanyPopup(false);
    history.push("/admin/settings/general");
    handleClickCustomer(customer);
  };

  const handleAddCompany = (): void => {
    createCustomer({
      shortname,
      name,
      timezone,
      language: language.value,
      realm,
    }).then((res) =>
      res.data
        ? setState({ createdCustomer: res.data })
        : dispatch(
            setAlert(JSON.stringify(res.e.response.data.errors), "error"),
          ),
    );
  };

  const handleCancel = (): void => {
    handleNewCompanyPopup(false);
    setState({
      shortname: "",
      name: "",
      language: { label: "german", value: "de" },
      timezone: "Europe/Zurich",
    });
  };

  return (
    <>
      <KDialog
        size="xs"
        title="New Company"
        open={open && !createdCustomer}
        primaryCTAonClick={() => handleAddCompany()}
        primaryCTAColor="primary"
        primaryCTAText="Create"
        secondaryCTAonClick={() => handleCancel()}
        secondaryCTAText="Cancel"
        hasOverflow
      >
        <KTextInput
          label="Company Short Name"
          placeholder="kyburz"
          helperText="Lowercase and underscore possible, example: au_post"
          onChange={handleChange}
          name="shortname"
          value={shortname}
        />
        <KTextInput
          label="Name"
          placeholder="Kyburz AG"
          helperText="Name that is displayed for the company"
          onChange={handleChange}
          name="name"
          value={name}
        />
        <KSelectAutocomplete
          label="Language"
          elements={languages}
          value={humanize(language.label)}
          onChange={handleSelectChange}
          name="language"
          style={{ width: 400 }}
        />
        <KSelectAutocomplete
          label="Timezone"
          elements={timezones}
          value={timezone}
          onChange={handleSelectChange}
          name="timezone"
          style={{ width: 400 }}
        />
        <KSelectAutocomplete
          label="Realm"
          elements={realmsOptions}
          value={realm}
          onChange={handleSelectChange}
          transformText={(s) => s}
          name="realm"
          style={{ width: 400 }}
        />
      </KDialog>
      <KDialog
        size="xs"
        title="Company created"
        open={!!createdCustomer}
        primaryCTAonClick={() => handleCustomerSwitch(createdCustomer)}
        primaryCTAColor="primary"
        primaryCTAText="OK"
      >
        <div className={classes.infoText}>
          Please wait about 10 minutes before copying the settings from an
          existing company to the new one.
        </div>
      </KDialog>
    </>
  );
};

export default NewCustomer;
